/** Turn snake_case / kebab identifiers into readable sentence-style words. */
export function humanizeIdentifier(value: string): string {
  const words = value
    .replace(/[:_/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  if (!words) return "";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const TERMINAL_STATUSES = new Set([
  "done",
  "preview:ready",
  "error",
  "preview:error",
  "idle",
]);

/** User-facing copy for internal generation / preview status codes. */
export function formatGenerationStatus(status: string): string {
  if (!status || status === "idle") return "";

  const exact: Record<string, string> = {
    connecting: "Connecting",
    starting: "Getting started",
    inventing_prompt: "Coming up with an idea",
    prompt_ready: "Idea ready",
    calling_provider: "Designing your preset",
    idempotent_replay: "Resuming where we left off",
    done: "Preset ready",
    error: "Something went wrong",
    "preview:ready": "Preview ready",
    "preview:error": "Preview failed",
  };

  if (exact[status]) return withProgress(exact[status], status);

  if (status.startsWith("policy:")) return withProgress("Checking your preset", status);
  if (status.startsWith("job:")) return withProgress("Rendering preview", status);
  if (status.startsWith("preview:")) {
    const state = status.slice("preview:".length);
    if (state === "queued") return withProgress("Preview queued", status);
    if (state === "running" || state === "claimed") return withProgress("Rendering preview", status);
    if (state === "failed") return "Preview failed";
    if (/^[a-f0-9-]+$/i.test(state)) return withProgress("Preparing preview", status);
    return withProgress(humanizeIdentifier(state), status);
  }
  if (status.startsWith("library:")) return withProgress("Loading preview", status);

  if (/^[a-z][a-z0-9_]*$/i.test(status)) {
    return withProgress(humanizeIdentifier(status), status);
  }

  return withProgress("Working", status);
}

function withProgress(label: string, rawStatus: string): string {
  if (TERMINAL_STATUSES.has(rawStatus)) return label;
  if (label.endsWith("…")) return label;
  return `${label}…`;
}

export function isGenerationStatusActive(status: string): boolean {
  const label = formatGenerationStatus(status);
  if (!label) return false;
  return label.endsWith("…");
}

/** Friendly copy for API / generation errors shown in the workspace. */
export function formatUserError(message: string): string {
  if (!message.trim()) return "Something went wrong.";

  if (message.startsWith("Policy rejected:")) {
    return "This preset didn't pass our safety checks. Try adjusting your prompt.";
  }
  if (message.startsWith("compile_failed:")) {
    return "We couldn't build the preset file. Try generating again.";
  }
  if (message === "Random prompt generation failed") {
    return "Couldn't come up with a random idea. Try again.";
  }
  if (message === "version_conflict") {
    return "This preset changed elsewhere. Refresh and try again.";
  }
  if (/^[a-z][a-z0-9_]*$/.test(message)) {
    return `${humanizeIdentifier(message)}.`;
  }

  return message;
}
